import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  saveLoanDetails,
  selectApplication,
  selectAppSaving,
  selectAppError,
  clearApplicationError
} from '../../../redux/slices/applicationSlice';
import { StepCard, StepActions, Button, FieldGrid } from '../../../components/stepCard';
import { Field, TextField, SelectField } from '../../../components/formField';
import ImportPanel from '../../../components/importPanel';
import { calculateLoanSummary, formatRupees } from '../../../utils/loanMath';
import PageTransition from '../../../components/pageTransition';
import { useCountUp } from '../../../hooks/useCountUp';
import { notify } from '../../../utils/notify';
import './index.scss';

const TENURE_OPTIONS = [12, 24, 36, 48, 60, 84].map((m) => ({
  value: m,
  label: `${m} months`
}));

const PURPOSE_OPTIONS = [
  { value: 'HOME_RENOVATION', label: 'Home renovation' },
  { value: 'MEDICAL_EMERGENCY', label: 'Medical emergency' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'VEHICLE_PURCHASE', label: 'Vehicle purchase' },
  { value: 'BUSINESS_EXPANSION', label: 'Business expansion' },
  { value: 'DEBT_CONSOLIDATION', label: 'Debt consolidation' }
];

const EMI_DAY_OPTIONS = [1, 5, 10, 15].map((d) => ({
  value: d,
  label: `${d}${d === 1 ? 'st' : 'th'} of the month`
}));

const MIN_AMOUNT = 50000;
const MAX_AMOUNT = 10000000;

const validate = (values) => {
  const errors = {};

  const amount = Number(values.loanAmount);
  if (!amount) errors.loanAmount = 'Enter the amount you need';
  else if (amount < MIN_AMOUNT) errors.loanAmount = `Minimum is ${formatRupees(MIN_AMOUNT)}`;
  else if (amount > MAX_AMOUNT) errors.loanAmount = `Maximum is ${formatRupees(MAX_AMOUNT)}`;

  if (!values.tenureMonths) errors.tenureMonths = 'Select a tenure';
  if (!values.loanPurpose) errors.loanPurpose = 'Select what the loan is for';
  if (!values.preferredEmiDay) errors.preferredEmiDay = 'Select your preferred EMI date';

  return errors;
};

const LoanStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const application = useSelector(selectApplication);
  const saving = useSelector(selectAppSaving);
  const serverError = useSelector(selectAppError);

  const [values, setValues] = useState({
    loanAmount: '',
    tenureMonths: '',
    loanPurpose: '',
    preferredEmiDay: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!application) return;
    setValues({
      loanAmount: application.loanAmount ?? '',
      tenureMonths: application.tenureMonths ?? '',
      loanPurpose: application.loanPurpose ?? '',
      preferredEmiDay: application.preferredEmiDay ?? ''
    });
  }, [application]);

  // Recomputed on every keystroke — same formula as the server, so what
  // the applicant sees here is what gets snapshotted on submit.
  const summary = useMemo(
    () =>
      calculateLoanSummary({
        loanAmount: values.loanAmount,
        tenureMonths: values.tenureMonths,
        monthlyIncome: application?.monthlyIncome,
        loanPurpose: values.loanPurpose
      }),
    [values.loanAmount, values.tenureMonths, values.loanPurpose, application?.monthlyIncome]
  );

  const setField = (name) => (e) => {
    const raw = e.target.value;
    const value = ['tenureMonths', 'preferredEmiDay'].includes(name) ? Number(raw) || '' : raw;

    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (serverError) dispatch(clearApplicationError());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) return;

    const result = await dispatch(
      saveLoanDetails({
        id: application.id,
        values: { ...values, loanAmount: Number(values.loanAmount) }
      })
    );

    if (saveLoanDetails.fulfilled.match(result)) {
      navigate('/apply/documents');
    } else {
      notify.error(result.payload);
    }
  };

  const animatedEmi = useCountUp(summary.emi ?? 0);
  const animatedInterest = useCountUp(summary.totalInterest ?? 0);
  const animatedFoir = useCountUp(summary.foirPct ?? 0);

  // FOIR above 50% is where lenders start declining. Warn, don't block —
  // the underwriter makes that call, not the form.
  const foirHigh = summary.foirPct !== null && summary.foirPct > 50;

  return (
    <PageTransition>
      <ImportPanel />

      <StepCard title="Loan requirements" description="Tell us how much you need and for how long.">
        <form onSubmit={handleSubmit} noValidate>
          <FieldGrid>
            <Field
              label="Loan amount (₹)"
              htmlFor="loanAmount"
              required
              hint={`Between ${formatRupees(MIN_AMOUNT)} and ${formatRupees(MAX_AMOUNT)}`}
              error={errors.loanAmount}>
              <TextField
                id="loanAmount"
                inputMode="numeric"
                placeholder="500000"
                value={values.loanAmount}
                onChange={setField('loanAmount')}
                error={errors.loanAmount}
              />
            </Field>

            <Field label="Tenure" htmlFor="tenureMonths" required error={errors.tenureMonths}>
              <SelectField
                id="tenureMonths"
                value={values.tenureMonths}
                onChange={setField('tenureMonths')}
                error={errors.tenureMonths}
                placeholder="Select"
                options={TENURE_OPTIONS}
              />
            </Field>

            <Field label="Loan purpose" htmlFor="loanPurpose" required error={errors.loanPurpose}>
              <SelectField
                id="loanPurpose"
                value={values.loanPurpose}
                onChange={setField('loanPurpose')}
                error={errors.loanPurpose}
                placeholder="Select"
                options={PURPOSE_OPTIONS}
              />
            </Field>

            <Field
              label="Preferred EMI date"
              htmlFor="preferredEmiDay"
              required
              error={errors.preferredEmiDay}>
              <SelectField
                id="preferredEmiDay"
                value={values.preferredEmiDay}
                onChange={setField('preferredEmiDay')}
                error={errors.preferredEmiDay}
                placeholder="Select"
                options={EMI_DAY_OPTIONS}
              />
            </Field>
          </FieldGrid>

          <div className="estimate">
            <div className="estimate__tile">
              <span className="estimate__label">Estimated EMI</span>
              <span className="estimate__value numeric">
                {summary.emi ? formatRupees(animatedEmi) : '—'}
              </span>
              <span className="estimate__sub">
                {values.loanPurpose ? `at ${summary.annualRatePct}% p.a.` : 'select a purpose'}
              </span>
            </div>

            <div className="estimate__tile">
              <span className="estimate__label">Total interest</span>
              <span className="estimate__value numeric">
                {summary.totalInterest ? formatRupees(animatedInterest) : '—'}
              </span>
              <span className="estimate__sub">over the full tenure</span>
            </div>

            <div className={`estimate__tile ${foirHigh ? 'estimate__tile--warn' : ''}`}>
              <span className="estimate__label">Share of income</span>
              <span className="estimate__value numeric">
                {summary.foirPct !== null ? `${animatedFoir.toFixed(2)}%` : '—'}
              </span>
              <span className="estimate__sub">
                {application?.monthlyIncome
                  ? `of ${formatRupees(application.monthlyIncome)}/month`
                  : 'Add your income in step 2'}
              </span>
            </div>
          </div>

          <p className="estimate__note">
            These figures are indicative. Your final rate is set by the underwriter after review.
          </p>

          {serverError && (
            <p className="form-error" role="alert">
              {serverError}
            </p>
          )}

          <StepActions onBack={() => navigate('/apply/kyc')}>
            <Button type="submit" loading={saving}>
              {saving ? 'Saving…' : 'Continue to documents →'}
            </Button>
          </StepActions>
        </form>
      </StepCard>
    </PageTransition>
  );
};

export default LoanStep;
